import React, { useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { PaginationControls } from "@optimizely/axiom";

import styles from "./BulkUploads.module.scss";
import { BulkUpload } from "../../../domain/BulkUpload";
import { BulkUploadDetails } from "../../components/BulkUploadDetails/BulkUploadDetails";
import { BulkUploadsTable } from "../../components/BulkUploadsTable/BulkUploadsTable";
import { ResetFilterPrompt } from "../../components/ResetFilterPrompt/ResetFilterPrompt";
import { SearchInput } from "../../components/SearchInput/SearchInput";
import { Sidebar } from "../../components/Sidebar/Sidebar";
import { useBulkUploads } from "../../../hooks/useBulkUploads/useBulkUploads";
import { useBulkUploadDetails } from "../../../hooks/useBulkUploadDetails/useBulkUploadDetails";
import { useDebounce } from "../../../hooks/useDebounce/useDebounce";
import { useUserContext } from "../../../providers/UserProvider";
import { DEFAULT_DEBOUNCE, MIN_SEARCH_QUERY_LENGTH } from "../../../constants/constants";

/**
 * Lists the organization's bulk user CSV uploads.
 *
 * Page, search and selected-upload state live in the URL (`page`, `q`, `fileId`) so the view is
 * linkable and survives a refresh. The detail sidebar is fetched by id rather than read out of the
 * list, so a `fileId` still resolves when its row is not on the current page. Access is gated at
 * the route level by `AuthorizedPageAccess` (feature flag + Super Admin), so this component
 * assumes the caller is already authorized.
 */
export const BulkUploads = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    // `?page=foo` parses to NaN and `?page=0`/`?page=-2` are out of range; either would flow into
    // the SWR key and the pagination controls, so fall back to the first page.
    const parsedPage = parseInt(searchParams.get("page") || "1", 10);
    const page = Number.isFinite(parsedPage) && parsedPage >= 1 ? parsedPage : 1;
    const queryParam = searchParams.get("q") || "";
    const selectedFileIdentifier = searchParams.get("fileId") || "";

    const { organizationId } = useUserContext();

    const {
        debouncedValue: debouncedSearchQuery,
        value: searchQuery,
        setValue: setSearchQuery
    } = useDebounce<string>(queryParam, DEFAULT_DEBOUNCE);

    const previousSearchWasValidRef = useRef(false);

    const isValidSearch = debouncedSearchQuery.length >= MIN_SEARCH_QUERY_LENGTH;

    const { uploads, pageCount, loading, error } = useBulkUploads({
        organizationId,
        page,
        query: isValidSearch ? debouncedSearchQuery : ""
    });

    // Seeds the sidebar so it paints from the row already on screen. Absent when the id came from
    // a shared link rather than a click, in which case the fetch is the only source.
    const selectedUploadFromList = uploads.find(({ fileIdentifier }) => fileIdentifier === selectedFileIdentifier);

    const {
        upload: selectedUpload,
        loading: detailsLoading,
        error: detailsError
    } = useBulkUploadDetails({
        organizationId,
        fileIdentifier: selectedFileIdentifier || undefined,
        fallback: selectedUploadFromList
    });

    // Mirror the debounced search into the URL, resetting to page 1 whenever the effective
    // search changes (including when it is cleared back below the minimum length).
    useEffect(() => {
        const wasValidSearch = previousSearchWasValidRef.current;

        if (isValidSearch || (wasValidSearch && !isValidSearch)) {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", "1");
                if (isValidSearch) {
                    newParams.set("q", debouncedSearchQuery);
                } else {
                    newParams.delete("q");
                }
                return newParams;
            });
        }

        previousSearchWasValidRef.current = isValidSearch;
    }, [debouncedSearchQuery, isValidSearch, setSearchParams]);

    const handlePagination = useCallback(
        (newPage: number) => {
            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("page", `${newPage}`);
                return newParams;
            });
        },
        [setSearchParams]
    );

    const handleUploadClick = useCallback(
        ({ upload }: { upload: BulkUpload }) => {
            const { fileIdentifier } = upload;
            // The id is the only handle the detail fetch has, so a row without one cannot be
            // opened at all — better to ignore the click than to push an empty `fileId`.
            if (!fileIdentifier) return;

            setSearchParams((prevParams) => {
                const newParams = new URLSearchParams(prevParams);
                newParams.set("fileId", fileIdentifier);
                return newParams;
            });
        },
        [setSearchParams]
    );

    const handleDetailsClose = useCallback(() => {
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.delete("fileId");
            return newParams;
        });
    }, [setSearchParams]);

    const resetFilters = useCallback(() => {
        setSearchQuery("");
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            newParams.set("page", "1");
            newParams.delete("q");
            return newParams;
        });
    }, [setSearchQuery, setSearchParams]);

    const showNoResults = !loading && !error && uploads.length === 0 && isValidSearch;
    const showEmptyState = !loading && !error && uploads.length === 0 && !isValidSearch;

    return (
        // The page root is marked so E2E can tell "the page rendered" from "the route guard denied
        // us" — both otherwise present only as a request that never happens.
        <div className={styles["bulk-uploads"]} data-cy="bulk-uploads-page" data-testid="bulk-uploads-page">
            <div className={styles["bulk-uploads__action-bar"]}>
                <div className={styles["bulk-uploads__search-input"]}>
                    <SearchInput
                        placeholder="Search uploads by file name..."
                        type="text"
                        value={searchQuery}
                        onChange={({ value }) => setSearchQuery(value)}
                        ariaLabel="Search uploads by file name"
                    />
                </div>
            </div>

            {showEmptyState && (
                <div data-testid="bulk-uploads-empty-state" data-cy="bulk-uploads-empty-state">
                    <p>No CSV files have been uploaded yet.</p>
                </div>
            )}

            {showNoResults && (
                <ResetFilterPrompt
                    data-testid="bulk-uploads-reset-filters"
                    data-cy="bulk-uploads-reset-filters"
                    onClick={resetFilters}
                    prompt="No uploads match your search."
                />
            )}

            {(loading || error || uploads.length > 0) && (
                <>
                    <BulkUploadsTable
                        uploads={uploads}
                        isLoading={loading}
                        error={Boolean(error)}
                        onUploadClick={handleUploadClick}
                        selectedFileIdentifier={selectedFileIdentifier}
                    />
                    {pageCount > 1 && (
                        <PaginationControls
                            className="anchor--bottom"
                            currentPage={page}
                            goToPage={handlePagination}
                            totalPages={pageCount}
                        />
                    )}
                </>
            )}

            {!!selectedFileIdentifier && (
                <Sidebar heading="Upload Details" isOpen onCancel={handleDetailsClose}>
                    {/* Keyed by id so picking a different row while the sidebar is open remounts the
                        details and drops the previous upload's tab selection — otherwise the next
                        upload opens on whichever tab the last one was left on. Keyed here rather
                        than on the Sidebar so the sidebar's own open state survives the switch. */}
                    <BulkUploadDetails
                        key={selectedFileIdentifier}
                        upload={selectedUpload}
                        isLoading={detailsLoading}
                        error={Boolean(detailsError)}
                    />
                </Sidebar>
            )}
        </div>
    );
};

BulkUploads.displayName = "BulkUploadsPage";
